/**
 * Employer branding sections of the public career site, saved on the firm (`jobBoardSections`) by the job
 * board customization page and shown below the header banner, in their order. Each one can also be shown
 * at the bottom of every offer. Texts are plain text in French and English (never HTML): the career pages
 * print them as text, line breaks kept.
 *
 * Every section has a title and an introduction; its items use the fields listed for its type in
 * CAREER_SECTION_ITEM_FIELDS (a testimonial's `value` is the person's name, a figure's `value` the number...).
 */
import { CAREER_BACKGROUND_PATTERNS, careerPatternId, sanitizeCareerImage } from './careerImage.ts'
import type { TCareerBackgroundPattern } from './careerImage.ts'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const CAREER_SECTION_TYPES = ['text', 'benefits', 'figures', 'testimonials', 'gallery', 'video', 'offices', 'process', 'faq'] as const
export type TCareerSectionType = (typeof CAREER_SECTION_TYPES)[number]

export const CAREER_TEXT_LANGUAGES = ['FR', 'EN'] as const
export type TCareerTextLanguage = (typeof CAREER_TEXT_LANGUAGES)[number]
export type TCareerText = Record<TCareerTextLanguage, string>

/** Icons offered to the benefits, drawn by the career pages */
export const CAREER_BENEFIT_ICONS = [
  'remote',
  'clock',
  'calendar',
  'health',
  'money',
  'gift',
  'training',
  'travel',
  'meal',
  'sport',
  'family',
  'team',
  'growth',
  'equipment',
  'mobility',
  'eco',
  'international',
  'wellbeing',
  'party',
  'star',
] as const
export type TCareerBenefitIcon = (typeof CAREER_BENEFIT_ICONS)[number]

export const CAREER_SECTION_ITEM_FIELD_KEYS = ['image', 'icon', 'value', 'title', 'text'] as const
export type TCareerSectionItemField = (typeof CAREER_SECTION_ITEM_FIELD_KEYS)[number]

/** Fields of the items of each section type, in the order of the form (no item for an empty list) */
export const CAREER_SECTION_ITEM_FIELDS: Record<TCareerSectionType, TCareerSectionItemField[]> = {
  text: [],
  benefits: ['icon', 'title', 'text'],
  figures: ['value', 'title'],
  testimonials: ['image', 'value', 'title', 'text'],
  gallery: ['image', 'title'],
  video: [],
  offices: [],
  process: ['title', 'text'],
  faq: ['title', 'text'],
}

export const CAREER_SECTION_LIMITS = {
  sections: 20,
  items: 24,
  /** Recruitment steps of one offer */
  steps: 12,
  offices: 200,
  title: 160,
  text: 4000,
  itemText: 1500,
  value: 40,
} as const

/** Built-in gradients of the section backgrounds (drawn by the career pages) */
export const CAREER_BACKGROUND_GRADIENTS = ['themeSoft', 'themeBold', 'mist', 'sunrise', 'lagoon', 'aurora', 'midnight', 'charcoal', 'navy'] as const
export type TCareerBackgroundGradient = (typeof CAREER_BACKGROUND_GRADIENTS)[number]

/** Background of a section: a gradient or an image (the image wins), and a pattern over it */
export type TCareerBackground = {
  gradient: TCareerBackgroundGradient | null
  /** Uploaded image or link (sanitizeCareerImage), patterns excluded */
  image: string | null
  pattern: TCareerBackgroundPattern | null
}

export const emptyCareerBackground = (): TCareerBackground => ({ gradient: null, image: null, pattern: null })

export const sanitizeCareerBackground = (value: unknown): TCareerBackground => {
  const input = isRecord(value) ? value : {}
  const image = sanitizeCareerImage(input.image)
  return {
    gradient: CAREER_BACKGROUND_GRADIENTS.includes(input.gradient as TCareerBackgroundGradient) ? input.gradient as TCareerBackgroundGradient : null,
    image: image && !careerPatternId(image) ? image : null,
    pattern: CAREER_BACKGROUND_PATTERNS.includes(input.pattern as TCareerBackgroundPattern) ? input.pattern as TCareerBackgroundPattern : null,
  }
}

export const hasCareerBackground = (background: TCareerBackground | null | undefined) =>
  !!(background && (background.gradient || background.image || background.pattern))

export type TCareerSectionItem = {
  id: string
  /** Testimonial photo, gallery picture */
  image: string | null
  /** Benefit icon */
  icon: TCareerBenefitIcon | null
  /** Figure ("350+"), testimonial name */
  value: string
  /** Benefit, figure label, testimonial role, gallery caption, process step, FAQ question */
  title: TCareerText
  /** Benefit details, testimonial quote, process step details, FAQ answer */
  text: TCareerText
}

export type TCareerSection = {
  id: string
  type: TCareerSectionType
  enabled: boolean
  /** Also shown at the bottom of every offer */
  showOnOffers: boolean
  title: TCareerText
  /** Introduction, or the whole text of a `text` section */
  text: TCareerText
  /** Illustration of a `text` section */
  image: string | null
  /** YouTube, Vimeo or video file link of a `video` section */
  url: string | null
  items: TCareerSectionItem[]
  /** Offices shown by an `offices` section on the offer list, all of them when empty */
  officeIds: string[]
  /** Title of an `offices` section on an offer page, which shows the offices of the offer */
  offerTitle: TCareerText
  background: TCareerBackground
}

export type TCareerVideoEmbed = { kind: 'iframe' | 'video', src: string }


const ID = /^[\w-]{1,40}$/
const OBJECT_ID = /^[0-9a-f]{24}$/
const YOUTUBE_ID = /^[\w-]{11}$/
const VIMEO_ID = /^\d{1,12}$/

const sanitizeString = (value: unknown, max: number) => (typeof value === 'string' ? value.slice(0, max) : '')

export const emptyCareerText = (): TCareerText => ({ FR: '', EN: '' })

const sanitizeText = (value: unknown, max: number): TCareerText => {
  const input = isRecord(value) ? value : {}
  return { FR: sanitizeString(input.FR, max), EN: sanitizeString(input.EN, max) }
}

const hasText = (value: TCareerText) => CAREER_TEXT_LANGUAGES.some(language => value[language].trim())

/**
 * Player of a video link: YouTube (privacy-enhanced mode, no cookie before playing), Vimeo (do not track)
 * or an mp4 / webm file over https. Any other link has none.
 */
export const careerVideoEmbed = (value: unknown): TCareerVideoEmbed | null => {
  if (typeof value !== 'string' || !value.trim() || value.length > 2048)
    return null
  let url: URL
  try {
    url = new URL(value.trim())
  }
  catch {
    return null
  }
  if (url.protocol !== 'https:')
    return null
  const host = url.hostname.replace(/^(www|m)\./, '')
  const path = url.pathname.split('/').filter(Boolean)
  let youtubeId: string | null | undefined
  if (host === 'youtu.be')
    youtubeId = path[0]
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com')
    youtubeId = path[0] === 'watch' ? url.searchParams.get('v') : ['embed', 'shorts', 'live'].includes(path[0]) ? path[1] : null
  if (youtubeId && YOUTUBE_ID.test(youtubeId))
    return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${youtubeId}` }
  const vimeoId = host === 'vimeo.com' ? path[0] : host === 'player.vimeo.com' && path[0] === 'video' ? path[1] : null
  if (vimeoId && VIMEO_ID.test(vimeoId))
    return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeoId}?dnt=1` }
  if (/\.(mp4|webm)$/i.test(url.pathname))
    return { kind: 'video', src: url.href }
  return null
}

const sanitizeItem = (value: unknown, fallbackId: string): TCareerSectionItem => {
  const input = isRecord(value) ? value : {}
  return {
    id: typeof input.id === 'string' && ID.test(input.id) ? input.id : fallbackId,
    image: sanitizeCareerImage(input.image),
    icon: CAREER_BENEFIT_ICONS.includes(input.icon as TCareerBenefitIcon) ? input.icon as TCareerBenefitIcon : null,
    value: sanitizeString(input.value, CAREER_SECTION_LIMITS.value),
    title: sanitizeText(input.title, CAREER_SECTION_LIMITS.title),
    text: sanitizeText(input.text, CAREER_SECTION_LIMITS.itemText),
  }
}

const sanitizeSection = (value: unknown, index: number): TCareerSection | null => {
  if (!isRecord(value) || !CAREER_SECTION_TYPES.includes(value.type as TCareerSectionType))
    return null
  const type = value.type as TCareerSectionType
  const id = typeof value.id === 'string' && ID.test(value.id) ? value.id : `section-${index}`
  const hasItems = CAREER_SECTION_ITEM_FIELDS[type].length > 0
  const items = hasItems && Array.isArray(value.items) ? value.items.slice(0, CAREER_SECTION_LIMITS.items) : []
  return {
    id,
    type,
    enabled: value.enabled !== false,
    showOnOffers: value.showOnOffers === true,
    title: sanitizeText(value.title, CAREER_SECTION_LIMITS.title),
    text: sanitizeText(value.text, CAREER_SECTION_LIMITS.text),
    image: type === 'text' ? sanitizeCareerImage(value.image) : null,
    url: type === 'video' && careerVideoEmbed(value.url) ? (value.url as string).trim() : null,
    items: items.map((item, itemIndex) => sanitizeItem(item, `${id}-${itemIndex}`)),
    officeIds: type === 'offices' && Array.isArray(value.officeIds)
      ? [...new Set(value.officeIds.filter((officeId): officeId is string => typeof officeId === 'string' && OBJECT_ID.test(officeId)))].slice(0, CAREER_SECTION_LIMITS.offices)
      : [],
    offerTitle: type === 'offices' ? sanitizeText(value.offerTitle, CAREER_SECTION_LIMITS.title) : emptyCareerText(),
    background: sanitizeCareerBackground(value.background),
  }
}

/** Keeps the known section types with their known fields, cut to the limits; anything else is dropped */
export const sanitizeCareerSections = (value: unknown): TCareerSection[] => {
  if (!Array.isArray(value))
    return []
  return value
    .slice(0, CAREER_SECTION_LIMITS.sections)
    .map(sanitizeSection)
    .filter((section): section is TCareerSection => !!section)
}

export const emptyCareerSectionItem = (id: string): TCareerSectionItem => ({
  id,
  image: null,
  icon: null,
  value: '',
  title: emptyCareerText(),
  text: emptyCareerText(),
})

/**
 * Recruitment steps of one offer (`careerSteps` of the offer), shown instead of those of the `process`
 * section. `null` when the offer keeps the steps of the career site.
 */
export const sanitizeCareerSteps = (value: unknown): TCareerSectionItem[] | null => {
  if (!Array.isArray(value))
    return null
  return value.slice(0, CAREER_SECTION_LIMITS.steps).map((item, index) => {
    const step = sanitizeItem(item, `step-${index}`)
    return { ...step, image: null, icon: null, value: '' }
  })
}

export const emptyCareerSection = (type: TCareerSectionType, id: string): TCareerSection => ({
  id,
  type,
  enabled: true,
  showOnOffers: false,
  title: emptyCareerText(),
  text: emptyCareerText(),
  image: null,
  url: null,
  items: [],
  officeIds: [],
  offerTitle: emptyCareerText(),
  background: emptyCareerBackground(),
})

/** Text in the visitor's language, or in the other one when it is empty */
export const careerText = (value: TCareerText | null | undefined, language: string) => {
  if (!value)
    return ''
  const wanted = language.toUpperCase().startsWith('EN') ? 'EN' : 'FR'
  return value[wanted].trim() ? value[wanted] : value[wanted === 'EN' ? 'FR' : 'EN']
}

/** Whether a section has something to show: an enabled section with a title, a text, items, a video... */
export const isCareerSectionShown = (section: TCareerSection) => {
  if (!section.enabled)
    return false
  if (section.type === 'offices')
    return true
  if (section.type === 'video')
    return !!careerVideoEmbed(section.url)
  if (section.type === 'text')
    return hasText(section.text) || !!section.image
  return section.items.length > 0
}
